import { useTask } from "../../../tasks/hooks/use-task"
import { useGetTaskPermissionsQuery } from "../../../tasks/api"
import CircularProgress from "@material-ui/core/CircularProgress"
import { paths } from "../../../paths"
import { useGetOrgMembershipsQuery } from "../../../memberships/api"
import TaskPermissionsManagerView from "../../../tasks/components/TaskPermissionsManager"
import { useRouter } from "../../../hooks"
import ErrorList from "../../../components/ErrorList"
import { useTranslation } from "react-i18next"

type RouteParams = { taskId: string }

const queryOptions = { refetchOnMountOrArgChange: true } as const

const TaskPermissionsRoute = () => {
  const translations = useTranslations()
  const router = useRouter()
  const { taskId } = router.query as RouteParams
  const { task, isLoading: isLoadingTask, isError: isErrorTask } = useTask(taskId, queryOptions)
  const {
    data: taskPermissions = [],
    isLoading: isLoadingPermissions,
    isError: isErrorPermissions,
  } = useGetTaskPermissionsQuery(taskId, { ...queryOptions, skip: !task || task.isUserTask })
  const {
    data: memberships = [],
    isLoading: isLoadingOrgMemberships,
    isError: isErrorOrgMemberships,
  } = useGetOrgMembershipsQuery(task?.maintainerId || "", {
    ...queryOptions,
    skip: !task || task.isUserTask,
  })

  if (isLoadingTask || isLoadingPermissions || isLoadingOrgMemberships) return <CircularProgress />
  if (isErrorTask) return <ErrorList errors={[translations.fetchErrorTask]} />
  if (!task) return <ErrorList errors={[translations.taskNotFoundError]} />
  if (isErrorPermissions) return <ErrorList errors={[translations.fetchErrorPermissions]} />
  if (isErrorOrgMemberships) return <ErrorList errors={[translations.fetchErrorMemberships]} />
  const taskBasic = `${paths.taskBasic(taskId)}${router.location.search}`

  return (
    <TaskPermissionsManagerView
      task={task}
      taskPermissions={taskPermissions}
      memberships={memberships}
      navigateToTask={() => router.history.push(taskBasic)}
    />
  )
}

const defaultTranslations = {
  taskNotFoundError: "Task not found",
  fetchErrorTask: "Failed to fetch task",
  fetchErrorPermissions: "Failed to fetch permissions",
  fetchErrorMemberships: "Failed to fetch memberships",
}

const useTranslations = (): typeof defaultTranslations => {
  const { t } = useTranslation("translation")

  return {
    taskNotFoundError: t("taskNotFoundError", defaultTranslations.taskNotFoundError),
    fetchErrorTask: t("fetchErrorTask", defaultTranslations.fetchErrorTask),
    fetchErrorMemberships: t("fetchErrorMemberships", defaultTranslations.fetchErrorMemberships),
    fetchErrorPermissions: defaultTranslations.fetchErrorPermissions,
  }
}

export default TaskPermissionsRoute
